/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Stream 클래스를 정의한다.
 * Ajax 로 데이터를 로딩하거나 처리하는 과정에 대한 진행상황을 모니터링하고, 그 결과를 받아온다.
 *
 * @file /src/utils/Stream.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 10. 14.
 */
import { Exceptions } from './Exceptions';
import { Language } from './Language';

export interface Params {
    [key: string]: string | number;
}

export interface Data {
    [key: string]: any;
}

export interface StreamData {
    success: boolean;
    message: string;
    current: number;
    total: number;
    data: { [key: string]: any };
    percentage: number;
    end: boolean;
    aborted: boolean;
    stream: Stream;
}

export interface Results {
    success: boolean;
    message?: string;
    total?: number;
    records?: any[];
    data?: Data;
    [key: string]: any;
}

export class Stream {
    static language: string;

    bytesTotal: number;
    bytesCurrent: number;
    total: number;
    current: number;
    chunks: Uint8Array[];
    controller: AbortController;
    signal: AbortSignal;
    aborted: boolean;
    rawData: string;

    /**
     * Stream 클래스를 정의한다.
     */
    constructor() {
        this.controller = new AbortController();
        this.signal = this.controller.signal;
        this.aborted = false;
    }

    /**
     * 지정된 시간만큼 자바스크립트를 지연시킨다.
     *
     * @param ms - 지연될 ms
     */
    static async sleep(ms: number): Promise<void> {
        return new Promise((r) => setTimeout(r, ms));
    }

    /**
     * 현재페이지의 아이모듈 언어코드를 가져온다. (zh-Hant → zt)
     *
     * @returns 언어코드
     */
    static getLanguage(): string {
        Stream.language ??= Language.getDefaultLangauge();
        return Stream.language;
    }

    /**
     * 멀티바이트 문자열의 정확한 크기(Bytes) 계산한다.
     *
     * @param text - 계산할 문자열
     * @returns 문자열 크기(Bytes)
     */
    getByteLength(text: string): number {
        if (text != undefined && text != '') {
            let bytes: number = 0;
            let char: number;
            for (let i = 0; (char = text.charCodeAt(i++)); bytes += char >> 11 ? 3 : char >> 7 ? 2 : 1);
            return bytes;
        } else {
            return 0;
        }
    }

    /**
     * 스트림 진행데이터를 가져온다.
     *
     * @returns 스트림 진행데이터
     */
    getStreamData(): StreamData {
        let chunksAll = new Uint8Array(this.bytesCurrent);
        let position = 0;
        for (const chunk of this.chunks) {
            chunksAll.set(chunk, position);
            position += chunk.length;
        }

        let response = new TextDecoder('utf-8').decode(chunksAll);
        const lines = response.split('\n');

        let latest = null;
        while (lines.length > 0) {
            let line = lines.pop();
            try {
                latest = JSON.parse(line.trim());
                if (typeof latest == 'object') break;
            } catch (e) {}
        }

        if (latest == null) {
            return this.setStreamData();
        }

        return this.setStreamData({
            ...latest,
            success: true,
            percentage: Math.max(100, (this.bytesCurrent / this.bytesTotal - 10000) * 100),
        });
    }

    /**
     * 스트림의 일부데이터로 진행데이터를 정리한다.
     *
     * @param results - 일부 결과 데이터
     * @returns 전체 결과 데이터
     */
    setStreamData(
        results: {
            success?: boolean;
            message?: string;
            current?: number;
            total?: number;
            data?: { [key: string]: any };
            percentage?: number;
            end?: boolean;
        } = null
    ): StreamData {
        return {
            success: results?.success ?? false,
            message: results?.message ?? null,
            current: results?.current ?? 0,
            total: results?.total ?? 0,
            data: results?.data ?? null,
            percentage: results?.percentage ?? 0,
            end: results?.end ?? false,
            aborted: this.aborted,
            stream: this,
        };
    }

    /**
     * 실제 스트림 요청을 처리한다.
     *
     * @param method - 요청방식
     * @param url - 요청주소
     * @param params - GET 데이터
     * @param data - 전송할 데이터
     * @param callback - 스트림 콜백함수
     * @returns 스트림 최종완료 결과
     */
    async #fetch(
        method: string = 'GET',
        url: string,
        params: Params = {},
        data: Data | FormData = {},
        callback: (stream: StreamData) => void
    ): Promise<Results> {
        const requestUrl = new URL(url, location.origin);
        for (const name in params) {
            if (params[name] === null) {
                requestUrl.searchParams.delete(name);
            } else {
                requestUrl.searchParams.append(name, params[name].toString());
            }
        }
        url = requestUrl.toString();

        const headers = {
            'X-Method': method,
            'Accept-Language': Stream.getLanguage(),
            'Accept': 'application/json',
        };

        if (method == 'SYNC') {
            method = 'GET';
        }

        if (method == 'EXCEL') {
            method = 'POST';
        }

        // Ajax 와 동일하게, 전송할 데이터가 있는 DELETE 요청은 POST 로 전송한다. (실제 요청방식은 X-Method 헤더로 전달)
        if (method == 'DELETE' && data !== null && (data instanceof FormData || Object.keys(data).length > 0)) {
            method = 'POST';
        }

        let body: string | FormData = null;
        if (method == 'POST' || method == 'PUT') {
            if (data instanceof FormData) {
                body = data;
            } else {
                headers['Content-Type'] = 'application/json; charset=utf-8';
                body = JSON.stringify(data);
            }
        }

        try {
            const response: Response = (await fetch(url, {
                signal: this.signal,
                method: method,
                headers: headers,
                body: body,
                cache: 'no-store',
                redirect: 'follow',
            }).catch(async (e: Error) => {
                throw Exceptions.code('FAILED_TO_LOAD_DATA', null, null, null, e);
            })) as Response;

            const reader = response.body.getReader();
            this.chunks = [];
            this.bytesCurrent = 0;
            this.bytesTotal = parseInt(response.headers.get('Content-Length') ?? '2010000', 10);
            this.total = parseInt(response.headers.get('X-Progress-Total') ?? '-1', 10);

            if (this.bytesTotal >= 0 && this.total >= 0) {
                callback(this.setStreamData({ success: true, current: 0, total: this.total }));
            }

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    const success = this.isSuccess();
                    let data = null;
                    if (success !== true) {
                        data = { success: false };
                    } else {
                        data = this.getStreamData();
                        callback(data);
                    }

                    const bufferIndex = this.rawData.indexOf('__OUT_OF_OUTPUT_BUFFER__');
                    let results: Results = null;
                    if (bufferIndex !== -1) {
                        const afterBuffer = this.rawData.substring(this.rawData.indexOf('\n', bufferIndex) + 1);
                        const start = afterBuffer.indexOf('{');
                        const end = afterBuffer.lastIndexOf('}');
                        if (start !== -1 && end > start) {
                            results = JSON.parse(afterBuffer.substring(start, end + 1)) as Results;
                        }
                    }
                    if (results !== null) {
                        return results;
                    } else {
                        return data as Results;
                    }
                }

                this.chunks.push(value);
                this.bytesCurrent += value.length;

                if (this.bytesTotal >= 0 && this.total >= 0) {
                    callback(this.getStreamData());
                }
            }
        } catch (e) {
            const data = this.setStreamData({ end: true });
            callback(data);
            return data;
        }
    }

    /**
     * 스트림 요청을 취소한다.
     */
    abort(): void {
        this.aborted = true;
        this.controller.abort();
    }

    /**
     * 스트림 데이터가 정상적으로 종료되었는지 확인한다.
     *
     * @return success - 성공여부 확인
     */
    isSuccess(): boolean | { [key: string]: any } {
        let chunksAll = new Uint8Array(this.bytesCurrent);
        let position = 0;
        for (const chunk of this.chunks) {
            chunksAll.set(chunk, position);
            position += chunk.length;
        }

        this.rawData = new TextDecoder('utf-8').decode(chunksAll);
        if (this.rawData.split('\n').pop().trim() == '@') {
            return true;
        } else {
            return false;
        }
    }

    /**
     * GET 요청을 스트림 요청과 함께 가져온다.
     *
     * @param url - 요청주소
     * @param params - GET 데이터
     * @param callback - 스트림 데이터를 받을 콜백함수
     * @returns 요청결과
     */
    static async get(url: string, params: Params = {}, callback: (stream: StreamData) => void): Promise<Results> {
        const stream = new Stream();
        return await stream.#fetch('GET', url, params, null, callback);
    }

    /**
     * POST 요청을 스트림 요청과 함께 가져온다.
     *
     * @param url - 요청주소
     * @param data - 전송할 데이터
     * @param params - GET 데이터
     * @param callback - 스트림 데이터를 받을 콜백함수
     * @returns 요청결과
     */
    static async post(
        url: string,
        data: Data | FormData = {},
        params: Params = {},
        callback: (stream: StreamData) => void
    ): Promise<Results> {
        const stream = new Stream();
        return await stream.#fetch('POST', url, params, data, callback);
    }

    /**
     * DELETE 요청을 스트림 요청과 함께 가져온다.
     *
     * @param url - 요청주소
     * @param params - GET 데이터
     * @param data - 전송할 데이터
     * @param callback - 스트림 데이터를 받을 콜백함수
     * @returns 요청결과
     */
    static async delete(
        url: string,
        params: Params = {},
        data: Data | FormData = null,
        callback: (stream: StreamData) => void
    ): Promise<Results> {
        const stream = new Stream();
        return await stream.#fetch('DELETE', url, params, data, callback);
    }

    /**
     * SYNC 요청을 스트림 요청과 함께 가져온다.
     *
     * @param url - 요청주소
     * @param params - GET 데이터
     * @param callback - 스트림 데이터를 받을 콜백함수
     * @returns 요청결과
     */
    static async sync(url: string, params: Params = {}, callback: (stream: StreamData) => void): Promise<Results> {
        const stream = new Stream();
        return await stream.#fetch('SYNC', url, params, null, callback);
    }

    /**
     * EXCEL 요청을 스트림 요청과 함께 가져온다.
     *
     * @param url - 요청주소
     * @param data - 전송할 데이터
     * @param params - GET 데이터
     * @param callback - 스트림 데이터를 받을 콜백함수
     * @returns 요청결과
     */
    static async excel(
        url: string,
        data: Data | FormData = {},
        params: Params = {},
        callback: (stream: StreamData) => void
    ): Promise<Results> {
        const stream = new Stream();
        return await stream.#fetch('EXCEL', url, params, data, callback);
    }
}
